// components/Parcours/QuizEngine.jsx

import { AnimatePresence, motion } from 'framer-motion';
import { Check, CheckCircle2, ChevronRight, SkipForward, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const QuizEngine = ({ 
  quizData, 
  mode = 'standard',
  onComplete,
  onFail,
  mediatorOnFail,
  onClose,
  embedded = false,
  learnerProfile,
}) => {
  const { darkMode } = useTheme();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [pendingResults, setPendingResults] = useState(null);

  const questions = quizData?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isMicro = mode === 'micro';
  const isDiagnostic = mode === 'diagnostic';
  
  // Couleur d'accent selon le mode
  const accent = isDiagnostic 
    ? { 
        text: darkMode ? 'text-emerald-400' : 'text-emerald-700',
        bg: darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50',
        border: darkMode ? 'border-emerald-500/30' : 'border-emerald-300',
        solid: darkMode ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-emerald-700 hover:bg-emerald-600',
        bar: 'bg-emerald-500',
        glow: 'shadow-[0_0_8px_rgba(16,185,129,0.4)]'
      }
    : mode === 'comprehension'
    ? {
        text: darkMode ? 'text-purple-400' : 'text-purple-700',
        bg: darkMode ? 'bg-purple-500/10' : 'bg-purple-50',
        border: darkMode ? 'border-purple-500/30' : 'border-purple-300',
        solid: darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-700 hover:bg-purple-600',
        bar: 'bg-purple-500',
        glow: 'shadow-[0_0_8px_rgba(168,85,247,0.4)]'
      }
    : {
        text: darkMode ? 'text-cyan-400' : 'text-blue-700',
        bg: darkMode ? 'bg-cyan-500/10' : 'bg-blue-50',
        border: darkMode ? 'border-cyan-500/30' : 'border-blue-300',
        solid: darkMode ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-blue-700 hover:bg-blue-600',
        bar: 'bg-cyan-500',
        glow: 'shadow-[0_0_8px_rgba(34,211,238,0.4)]'
      };

  const getAnswerKey = (q, idx) => q._id || q.text || `q-${idx}`;

  const handleAnswer = (value) => {
    const key = getAnswerKey(currentQuestion, currentQuestionIndex);
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const checkAnswer = (q, userAnswer) => {
    if (!q || userAnswer === undefined || userAnswer === null) return false;

    if (q.type === 'multiple-choice') {
      const correctObj = q.options?.find(opt => opt.isCorrect === true || opt.correct === true);
      if (correctObj) return String(userAnswer).trim() === String(correctObj.text || correctObj).trim();
      if (typeof q.correctAnswer === 'number' && q.options?.[q.correctAnswer]) {
        const correctText = q.options[q.correctAnswer];
        return String(userAnswer).trim() === String(typeof correctText === 'string' ? correctText : correctText.text || '').trim();
      }
      return false;
    }
    if (q.type === 'true-false') {
      let correctBool = typeof q.correctAnswer === 'boolean' 
        ? q.correctAnswer 
        : ['vrai','true'].includes(String(q.correctAnswer).toLowerCase());
      return (userAnswer === 'Vrai') === correctBool;
    }
    if (q.type === 'open') return userAnswer && String(userAnswer).trim().length > 0;
    return false;
  };
  
  const getAdaptiveFeedback = (question) => {
    // 1. Priorité : adaptiveFeedback selon emotionSignal
    if (question.adaptiveFeedback && learnerProfile?.emotionSignal) {
      const key = `ifEmotionProfile_${learnerProfile.emotionSignal}`;
      if (question.adaptiveFeedback[key]) return question.adaptiveFeedback[key];
    }
    // 2. Priorité : adaptiveFeedback selon mode (renforcement etc.)
    if (question.adaptiveFeedback && learnerProfile?.mode) {
      const key = `ifMode_${learnerProfile.mode}`;
      if (question.adaptiveFeedback[key]) return question.adaptiveFeedback[key];
    }
    // 3. Fallback : feedback standard (ou construit)
    if (question.feedback) return question.feedback;
    if (question.errorAnalysis?.[0]?.remediation) {
      return question.errorAnalysis[0].remediation;
    }
    return null;
  };

  const handleSubmit = () => {
    let correctCount = 0;
    const totalPoints = questions.reduce((sum, q) => sum + (q.points || 10), 0);
    let score = 0;
    const userAnswers = { ...answers };

    questions.forEach((q, idx) => {
      const key = getAnswerKey(q, idx);
      if (checkAnswer(q, answers[key])) {
        score += q.points || 10;
        correctCount++;
      }
    });

    const percentage = totalPoints > 0 ? Math.round((score / totalPoints) * 100) : 0;
    const passed = percentage >= (quizData?.passingScore || 70);

    const quizResults = {
      score: percentage,
      passed,
      totalQuestions: questions.length,
      correctAnswers: correctCount,
      answers: userAnswers,
      timestamp: new Date().toISOString(),
    };

    setResult({ score: percentage, passed, correctCount });
    setSubmitted(true);
    setPendingResults(quizResults);

    //if (passed && onComplete) onComplete(quizResults);
    //if (!passed && onFail) onFail(quizResults);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const currentAnswerKey = getAnswerKey(currentQuestion, currentQuestionIndex);
  const currentAnswer = answers[currentAnswerKey];
  const isAnswered = currentAnswer !== undefined && currentAnswer !== null && currentAnswer !== '';

  // ── RÉSULTAT ──────────────────────────────────────────────────────────────
  if (submitted && result) {
    const questionsDetail = questions.map((q, idx) => {
      const key = getAnswerKey(q, idx);
      const userAnswer = answers[key];
      const isCorrect = checkAnswer(q, userAnswer);
      const feedback = getAdaptiveFeedback(q);
      return { question: q, userAnswer, isCorrect, feedback };
    });

    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }} 
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center overflow-hidden"
      >
        <div className="h-full flex flex-col overflow-hidden">
        {/* En-tête résultat (fixe) */}
        <div className="flex flex-col items-center text-center gap-4 shrink-0 px-4 pt-4 sm:px-8 sm:pt-8">
          {/* icône + score + barre + statut (inchangés) */}
          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl flex items-center justify-center border-2 ${
            result.passed 
              ? darkMode ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400' 
                        : 'border-emerald-400 bg-emerald-50 text-emerald-600'
              : darkMode ? 'border-red-500/40 bg-red-500/10 text-red-400' 
                        : 'border-red-400 bg-red-50 text-red-600'
          }`}>
            {result.passed 
              ? <CheckCircle2 size={36} strokeWidth={1.5} className="hidden sm:block" />
              : <XCircle size={36} strokeWidth={1.5} className="hidden sm:block" />
            }
            {result.passed 
              ? <CheckCircle2 size={28} strokeWidth={1.5} className="sm:hidden" />
              : <XCircle size={28} strokeWidth={1.5} className="sm:hidden" />
            }
          </div>

          <div>
            <div className={`text-[10px] font-mono uppercase tracking-[0.4em] mb-2 ${
              darkMode ? 'text-white/30' : 'text-gray-400'
            }`}>
              Score_Final
            </div>
            <div className={`text-5xl sm:text-7xl font-black font-mono tracking-tighter ${accent.text}`}>
              {result.score}<span className="text-2xl sm:text-3xl opacity-50">%</span>
            </div>
            <div className={`text-[11px] font-mono mt-3 ${
              darkMode ? 'text-white/30' : 'text-gray-500'
            }`}>
              {result.correctCount} / {questions.length} réponses correctes
            </div>
          </div>

          <div className={`w-48 sm:w-64 h-1.5 rounded-full overflow-hidden ${
            darkMode ? 'bg-white/5' : 'bg-gray-200'
          }`}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${result.score}%` }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              className={`h-full ${result.passed ? 'bg-emerald-500' : 'bg-red-500'}`}
            />
          </div>

          <div className={`px-4 py-2 rounded-lg border text-[10px] font-black uppercase tracking-widest font-mono ${
            result.passed 
              ? darkMode 
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' 
                : 'border-emerald-400/60 bg-emerald-50 text-emerald-700'
              : darkMode 
                ? 'border-red-500/30 bg-red-500/10 text-red-400' 
                : 'border-red-400/60 bg-red-50 text-red-700'
          }`}>
            {result.passed ? '✓ Validation_Confirmée' : '✗ Seuil_Non_Atteint'}
          </div>
        </div>

        {/* DÉTAIL DES RÉPONSES (scrollable) */}
        <div className="flex-1 overflow-y-auto no-scrollbar w-full px-4 sm:px-8 mt-6 space-y-3">
          <h3 className={`text-[10px] font-mono uppercase tracking-[0.3em] ${darkMode ? 'text-white/40' : 'text-gray-500'} mb-2`}>
            Détail des réponses
          </h3>
          {questionsDetail.map((detail, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * idx }}
              className={`rounded-xl border p-4 ${
                darkMode ? 'border-white/5 bg-white/[0.02]' : 'border-slate-200 bg-white'
              }`}
            >
              {/* ... (contenu de la question, inchangé) */}
              <div className="flex items-start gap-3">
                <div className={`w-6 h-6 shrink-0 rounded-full border flex items-center justify-center text-xs ${
                  detail.isCorrect
                    ? darkMode 
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400' 
                      : 'border-emerald-300 bg-emerald-50 text-emerald-600'
                    : darkMode 
                      ? 'border-red-500/40 bg-red-500/10 text-red-400' 
                      : 'border-red-300 bg-red-50 text-red-600'
                }`}>
                  {detail.isCorrect ? <Check size={14} /> : <X size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {detail.question.text}
                  </p>
                  {detail.userAnswer !== undefined && detail.userAnswer !== null && (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Votre réponse : <span className="font-mono">{String(detail.userAnswer)}</span>
                    </p>
                  )}
                  {detail.feedback && (
                    <div className={`mt-2 p-2 rounded-lg text-xs font-mono leading-relaxed ${
                      detail.isCorrect
                        ? darkMode 
                          ? 'bg-emerald-500/5 text-emerald-300 border border-emerald-500/10' 
                          : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : darkMode 
                          ? 'bg-red-500/5 text-red-300 border border-red-500/10' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                      {detail.feedback}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA (toujours visible) */}
        <div className="shrink-0 px-4 pb-4 sm:px-8 sm:pb-8 flex justify-center">
          <button 
            onClick={() => {
              if (pendingResults) {
                if (pendingResults.passed) onComplete?.(pendingResults);
                else onFail?.(pendingResults);
              }
              onClose?.();
            }}
            className={`flex items-center gap-3 px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] text-white transition-all ${accent.solid} ${accent.glow}`}
          >
            Continuer <ChevronRight size={14} />
          </button>
        </div>
        </div>
      </motion.div>
    );
  }

  if (!currentQuestion) return null;

  // ── QUIZ ──────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col">

      {/* BARRE DE PROGRESSION */}
      <div className={`shrink-0 px-4 sm:px-8 py-3 sm:py-4 border-b ${
        darkMode ? 'border-white/5' : 'border-slate-200/70'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[9px] font-mono uppercase tracking-widest ${
            darkMode ? 'text-white/20' : 'text-gray-400'
          }`}>
            Q_{String(currentQuestionIndex + 1).padStart(2, '0')} / {String(questions.length).padStart(2, '0')}
          </span>
          <span className={`text-[9px] font-mono ${accent.text}`}>
            Seuil : {quizData?.passingScore || 70}%
          </span>
        </div>
        
        {/* Progress segments */}
        <div className="flex gap-1">
          {questions.map((_, i) => (
            <div 
              key={i} 
              className={`flex-1 h-1 rounded-full transition-all duration-500 ${
                i < currentQuestionIndex 
                  ? accent.bar
                  : i === currentQuestionIndex
                  ? `${accent.bar} opacity-50`
                  : darkMode ? 'bg-white/5' : 'bg-gray-200'
              }`} 
            />
          ))}
        </div>
      </div>

      {/* QUESTION */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.2 }}
            className="p-4 sm:p-8 space-y-6 sm:space-y-8"
          >
            {/* Texte de la question */}
            <div className="space-y-2">
              <div className={`text-[9px] font-mono uppercase tracking-[0.3em] ${accent.text}`}>
                Question_{String(currentQuestionIndex + 1).padStart(2, '0')}
              </div>
              <p className={`text-lg sm:text-xl font-bold leading-snug tracking-tight ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {currentQuestion.text}
              </p>
            </div>

            {/* OPTIONS : Multiple choice */}
            {currentQuestion.type === 'multiple-choice' && (
              <div className="space-y-2">
                {currentQuestion.options?.map((opt, idx) => {
                  const optText = typeof opt === 'string' ? opt : opt.text;
                  const isSelected = currentAnswer === optText;
                  return (
                    <motion.button
                      key={idx}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleAnswer(optText)}
                      className={`w-full p-3 sm:p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between gap-3 group ${
                        isSelected 
                          ? `${accent.border} ${accent.bg} ${accent.glow}` 
                          : darkMode 
                            ? 'border-white/5 bg-white/[0.02] hover:border-white/10 hover:bg-white/5' 
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {/* Lettre option */}
                      <div className={`w-6 h-6 sm:w-7 sm:h-7 shrink-0 rounded-lg border flex items-center justify-center text-[10px] font-black font-mono transition-all ${
                        isSelected 
                          ? `${accent.border} ${accent.text}` 
                          : darkMode 
                            ? 'border-white/10 text-white/20' 
                            : 'border-slate-300 text-gray-400'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </div>
                      
                      <span className={`flex-1 text-sm font-medium transition-colors ${
                        isSelected 
                          ? darkMode ? 'text-white' : 'text-gray-900'
                          : darkMode ? 'text-slate-400 group-hover:text-slate-200' : 'text-gray-600 group-hover:text-gray-900'
                      }`}>
                        {optText}
                      </span>

                      {/* Indicateur sélection */}
                      <div className={`w-4 h-4 rounded-full border-2 shrink-0 transition-all ${
                        isSelected 
                          ? `${accent.border.replace('border-', 'border-')} ${accent.bar.replace('bg-', 'bg-')}` 
                          : darkMode ? 'border-white/10' : 'border-slate-300'
                      }`} />
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* OPTIONS : True/False */}
            {currentQuestion.type === 'true-false' && (
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {['Vrai', 'Faux'].map(val => (
                  <motion.button
                    key={val}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(val)}
                    className={`py-6 sm:py-10 rounded-2xl border-2 font-black uppercase tracking-widest text-sm transition-all ${
                      currentAnswer === val 
                        ? `${accent.border} ${accent.bg} ${accent.text} ${accent.glow}` 
                        : darkMode 
                          ? 'border-white/5 bg-white/[0.02] text-white/20 hover:border-white/10 hover:text-white/60' 
                          : 'border-slate-200 bg-white text-gray-400 hover:border-slate-300 hover:text-gray-600'
                    }`}
                  >
                    {val}
                  </motion.button>
                ))}
              </div>
            )}

            {/* OPTIONS : Open */}
            {currentQuestion.type === 'open' && (
              <textarea
                value={currentAnswer || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                placeholder="Entrez votre réponse..."
                rows={4}
                className={`w-full p-4 rounded-xl border-2 font-mono text-sm outline-none transition-all resize-none ${
                  darkMode 
                    ? `bg-white/[0.02] border-white/5 text-white placeholder-white/20 focus:${accent.border}` 
                    : `bg-white border-slate-200 text-gray-900 placeholder-gray-400 focus:${accent.border}`
                }`}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* FOOTER */}
      <div className={`shrink-0 px-4 sm:px-8 py-3 sm:py-4 border-t flex items-center justify-between ${
        darkMode ? 'border-white/5 bg-black/10' : 'border-slate-200/70 bg-slate-50/50'
      }`}>
        
        {/* Micro mode : skip autorisé */}
        {isMicro && (
          <button
            onClick={() => {
              if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(i => i + 1);
              } else {
                handleSubmit();
              }
            }}
            className={`flex items-center gap-2 text-[9px] font-mono uppercase tracking-widest ${
              darkMode ? 'text-white/20 hover:text-white/50' : 'text-gray-400 hover:text-gray-600'
            } transition-colors`}
          >
            <SkipForward size={12} /> Passer
          </button>
        )}
        
        {!isMicro && <div />}

        <button
          onClick={handleNext}
          disabled={!isAnswered}
          className={`flex items-center gap-2 sm:gap-3 px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl font-black uppercase tracking-widest text-[10px] text-white transition-all disabled:opacity-20 disabled:cursor-not-allowed ${accent.solid} ${isAnswered ? accent.glow : ''}`}
        >
          {currentQuestionIndex === questions.length - 1 ? 'Valider' : 'Suivant'}
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default QuizEngine;